import { makeAutoObservable, runInAction } from 'mobx'
import { 
  submitScore, 
  getLeaderboard, 
  getTopPlayers, 
  LeaderboardEntry 
} from '../lib/supabase'
import { authStore } from './authStore'
import { profileStore } from './profileStore'

class LeaderboardStore {
  entries: LeaderboardEntry[] = []
  topPlayers: LeaderboardEntry[] = []
  loading = false
  error: string | null = null
  currentGame: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  async fetchLeaderboard(game?: string) {
    this.loading = true
    this.error = null
    this.currentGame = game || null

    try {
      const { data, error } = await getLeaderboard(game)
      
      runInAction(() => {
        if (error) {
          this.error = error.message
        } else {
          this.entries = data || []
        }
        this.loading = false
      })
    } catch (e) {
      runInAction(() => {
        this.error = 'Failed to fetch leaderboard'
        this.loading = false
      })
    }
  }

  async fetchTopPlayers() {
    this.loading = true
    
    try {
      const { data, error } = await getTopPlayers()
      
      runInAction(() => {
        if (!error && data) {
          this.topPlayers = data
        }
        this.loading = false
      })
    } catch (e) {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  async submitGameScore(game: string, score: number, level: number) {
    if (!authStore.userId) {
      console.log('[Leaderboard] No userId, skipping submit')
      return
    }

    const entry: Omit<LeaderboardEntry, 'id' | 'created_at'> = {
      user_id: authStore.userId,
      username: profileStore.username || `Player ${authStore.userId.slice(-4)}`,
      emoji: profileStore.emoji,
      flag: profileStore.flag,
      score,
      game,
      level,
    }

    console.log(`[Leaderboard] Submitting ${game} score: ${score} (level: ${level})`, {
      userId: authStore.userId,
      username: entry.username,
      isAuthenticated: authStore.isAuthenticated
    })

    // Actualizar inmediatamente de forma optimista (antes de esperar al servidor)
    runInAction(() => {
      // Remover entradas anteriores del mismo usuario en el mismo juego si el score es mayor
      const filtered = this.entries.filter(e => 
        !(e.user_id === authStore.userId && e.game === game && e.score <= score)
      )
      
      this.entries = [
        { ...entry, id: Date.now().toString(), created_at: new Date().toISOString() },
        ...filtered
      ].sort((a, b) => b.score - a.score).slice(0, 50)
    })

    try {
      const { data, error } = await submitScore(entry)
      
      if (error) {
        console.error('[Leaderboard] Error submitting score:', error)
      } else {
        console.log('[Leaderboard] Score saved successfully:', data)
        // Refetch para sincronizar con el servidor
        setTimeout(() => this.fetchLeaderboard(game), 500)
      }
    } catch (e) {
      console.error('[Leaderboard] Failed to submit score:', e)
    }
  }

  // Calcular qué ranking tendría un score dado (excluye al usuario actual para comparación justa)
  getRankForScore(score: number, game?: string): number {
    let entries = game 
      ? this.entries.filter(e => e.game === game)
      : this.entries
    
    // Excluir entradas del usuario actual para calcular la posición real
    if (authStore.userId) {
      entries = entries.filter(e => e.user_id !== authStore.userId)
    }
    
    const sorted = [...entries].sort((a, b) => b.score - a.score)
    
    // Encontrar la posición donde este score quedaría
    let position = 1
    for (const entry of sorted) {
      if (score > entry.score) break
      position++
    }
    
    return position
  }

  get gameLeaderboard() {
    if (!this.currentGame) return this.entries
    return this.entries.filter(e => e.game === this.currentGame)
  }

  get userBestScore() {
    if (!authStore.userId) return 0
    const userEntries = this.entries.filter(e => e.user_id === authStore.userId)
    return userEntries.length > 0 ? Math.max(...userEntries.map(e => e.score)) : 0
  }

  get userRank() {
    if (!authStore.userId) return null
    const sorted = [...this.entries].sort((a, b) => b.score - a.score)
    const index = sorted.findIndex(e => e.user_id === authStore.userId)
    return index >= 0 ? index + 1 : null
  }
}

export const leaderboardStore = new LeaderboardStore()
